import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import Layouts from "../../Layouts";
import HimsModuleCards from "../NewDashboard/HimsModuleCards";
import { FaAmbulance, FaSync } from "react-icons/fa";
import { MdOutlineEmergency } from "react-icons/md";
import {
  TbHeartRateMonitor,
  TbAlertTriangle,
  TbShieldCheck,
  TbCalendarPlus,
} from "react-icons/tb";
import EmergencyService from "../../../Services/EmergencyService";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import EmergencyStats from "./EmergencyStats";
import EmergencyFilters from "./EmergencyFilters";
import EmergencyTable from "./EmergencyTable";
import EmergencyDetailModal from "./EmergencyDetailModal";

export default function EmergencyPage() {
  const history = useHistory();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [wardFilter, setWardFilter] = useState("All");
  const [date, setDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const storedLogin =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const user = storedLogin ? JSON.parse(storedLogin)?.user : null;

  const extractList = (res) => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.list)) return res.data.list;
    if (Array.isArray(res?.data?.emergencyList)) return res.data.emergencyList;
    if (Array.isArray(res)) return res;
    return [];
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const payload = { pageNo: 1, pageSize: 50 };
      if (user?.account_Id) payload.account_Id = user.account_Id;
      if (user?.clinic_Id) payload.clinic_Id = user.clinic_Id;

      const res = await EmergencyService.getEmergencyVisitList(payload);
      const data = extractList(res);

      if (data.length > 0) {
        setPatients(data);
      } else {
        const fallbackRes = await EmergencyService.getEmergencyVisitList({
          pageNo: 1,
          pageSize: 50,
        });
        const fallbackData = extractList(fallbackRes);
        setPatients(fallbackData);
      }
    } catch (err) {
      console.error("Failed to fetch emergency visit list with params:", err);
      try {
        const fallbackRes = await EmergencyService.getEmergencyVisitList({
          pageNo: 1,
          pageSize: 10,
        });
        const fallbackData = extractList(fallbackRes);
        setPatients(fallbackData);
      } catch (fallbackErr) {
        console.error("Fallback fetch also failed:", fallbackErr);
        setPatients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  /* ─── Compute Filter Dropdown Options ─── */
  const availableStatuses = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      const st = p.status || p.admissionStatus;
      if (st) set.add(st);
    });
    if (set.size === 0) {
      return [
        "All",
        "REGISTERED",
        "UNDER_TREATMENT",
        "OBSERVATION",
        "STABLE",
        "DISCHARGED",
      ];
    }
    return ["All", ...Array.from(set)];
  }, [patients]);

  const availableWards = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      const w = p.wardId || p.wardCode;
      if (w) set.add(w);
    });
    return ["All", ...Array.from(set)];
  }, [patients]);

  /* ─── Dynamic Stats Calculation ─── */
  const totalCount = patients.length;

  const criticalCount = patients.filter((p) => {
    const t = (p.triageLevel || "").toUpperCase();
    const s = (p.status || "").toUpperCase();
    return t === "HIGH" || s.includes("CRITICAL");
  }).length;

  const urgentCount = patients.filter((p) => {
    const t = (p.triageLevel || "").toUpperCase();
    const s = (p.status || "").toUpperCase();
    return t === "MEDIUM" || s.includes("UNDER_TREATMENT");
  }).length;

  const stableCount = patients.filter((p) => {
    const t = (p.triageLevel || "").toUpperCase();
    const s = (p.status || "").toUpperCase();
    return t === "LOW" || s === "STABLE" || s === "REGISTERED";
  }).length;

  const admittedTodayCount = patients.filter((p) => {
    const arrDate = p.arrivalDate || p.createdDate;
    if (!arrDate) return false;
    return (
      dayjs(arrDate).isSame(dayjs(), "day") ||
      dayjs(arrDate).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
    );
  }).length;

  const statCards = [
    {
      label: "Total Patients",
      value: String(totalCount).padStart(2, "0"),
      pct: `${totalCount > 0 ? "100%" : "0%"}`,
      up: true,
      icon: <TbHeartRateMonitor className="text-[#EF4444] text-lg" />,
      iconBg: "#FEE2E2",
      chart: "red",
    },
    {
      label: "Critical",
      value: String(criticalCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((criticalCount / totalCount) * 100) : 0
      }%`,
      up: criticalCount > 0,
      icon: <TbAlertTriangle className="text-[#EF4444] text-lg" />,
      iconBg: "#FEE2E2",
      chart: "red",
    },
    {
      label: "Urgent",
      value: String(urgentCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((urgentCount / totalCount) * 100) : 0
      }%`,
      up: true,
      icon: <MdOutlineEmergency className="text-[#F59E0B] text-lg" />,
      iconBg: "#FEF3C7",
      chart: "amber",
    },
    {
      label: "Stable",
      value: String(stableCount).padStart(2, "0"),
      pct: `${
        totalCount > 0 ? Math.round((stableCount / totalCount) * 100) : 0
      }%`,
      up: stableCount > 0,
      icon: <TbShieldCheck className="text-[#22C55E] text-lg" />,
      iconBg: "#DCFCE7",
      chart: "green",
    },
    {
      label: "Admitted Today",
      value: String(admittedTodayCount).padStart(2, "0"),
      pct: "Today",
      up: admittedTodayCount > 0,
      icon: <TbCalendarPlus className="text-[#8B5CF6] text-lg" />,
      iconBg: "#F3E8FF",
      chart: "purple",
    },
  ];

  /* ─── Client Filter Logic ─── */
  const filtered = useMemo(() => {
    return patients.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (p.patientName || p.name || "").toLowerCase().includes(q) ||
        (p.patientId || "").toLowerCase().includes(q) ||
        (p.emergencyId || "").toLowerCase().includes(q) ||
        (p.mobile || "").includes(q) ||
        (p.complaint || p.chiefComplaint || "").toLowerCase().includes(q) ||
        (p.doctor || p.doctorId || "").toLowerCase().includes(q) ||
        (p.driverName || "").toLowerCase().includes(q);

      const statusVal = p.status || p.admissionStatus || "";
      const matchStatus =
        statusFilter === "All" ||
        statusVal.toLowerCase() === statusFilter.toLowerCase();

      const wardVal = p.wardId || p.wardCode || "";
      const matchWard =
        wardFilter === "All" ||
        wardVal.toLowerCase() === wardFilter.toLowerCase();

      const matchDate =
        !date ||
        (p.arrivalDate &&
          (p.arrivalDate.includes(date) ||
            dayjs(p.arrivalDate).format("YYYY-MM-DD").includes(date) ||
            dayjs(p.arrivalDate).format("DD/MM/YYYY").includes(date))) ||
        (p.createdDate &&
          (p.createdDate.includes(date) ||
            dayjs(p.createdDate).format("YYYY-MM-DD").includes(date) ||
            dayjs(p.createdDate).format("DD/MM/YYYY").includes(date)));

      return matchSearch && matchStatus && matchWard && matchDate;
    });
  }, [patients, search, statusFilter, wardFilter, date]);

  /* ─── Pagination ─── */
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setWardFilter("All");
    setDate("");
    setCurrentPage(1);
  };

  const handleViewPatient = (patient) => {
    setSelectedPatient(patient);
  };

  const handleNavigateInpatient = (patient) => {
    if (patient?.patientId) {
      history.push(
        APP_ROUTES.INPATIENTDETAIL.replace(":id", patient.patientId),
        { emergency: patient }
      );
    }
  };

  return (
    <Layouts>
      <div className="p-4">
        {/* ── Common HIMS Module Cards ── */}
        <div className="mb-5">
          <HimsModuleCards
            activeId="emergency"
            counts={{
              emergency_critical: criticalCount,
              emergency_urgent: urgentCount,
            }}
          />
        </div>

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: "#FEE2E2" }}
            >
              <FaAmbulance className="text-[#EF4444] text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#333333] font-sans">
                Emergency
              </h1>
             
            </div>
          </div>
          <button
            onClick={fetchPatients}
            disabled={loading}
            className="flex items-center gap-2 border bg-white rounded-lg px-3 py-2 text-sm font-sans text-gray-700 hover:bg-gray-50 transition shadow-sm"
            title="Refresh patient list"
          >
            <FaSync
              className={`w-4 h-4 text-[#189FAA] ${
                loading ? "animate-spin" : ""
              }`}
            />
            <span>Refresh</span>
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <EmergencyStats statCards={statCards} />

        {/* ── Filters ── */}
        {/* <EmergencyFilters
          search={search}
          onSearchChange={(val) => {
            setSearch(val);
            setCurrentPage(1);
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(val) => {
            setStatusFilter(val);
            setCurrentPage(1);
          }}
          availableStatuses={availableStatuses}
          wardFilter={wardFilter}
          onWardFilterChange={(val) => {
            setWardFilter(val);
            setCurrentPage(1);
          }}
          availableWards={availableWards}
          date={date}
          onDateChange={(val) => {
            setDate(val);
            setCurrentPage(1);
          }}
          onClearFilters={handleClearFilters}
        /> */}

        {/* ── Table Component ── */}
        <EmergencyTable
          loading={loading}
          paginatedList={paginatedList}
          filteredCount={filtered.length}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={(sz) => {
            setPageSize(sz);
            setCurrentPage(1);
          }}
          onViewPatient={handleViewPatient}
        />

        {/* ── Detail View Modal Component ── */}
        <EmergencyDetailModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onNavigateInpatient={handleNavigateInpatient}
        />
      </div>
    </Layouts>
  );
}
